/**
 * Top-level staff Portal modules and personal default-start preference.
 * Access is resolved from primary + secondary roles (see userRoles / procurementInventory).
 */

export const PORTAL_MODULES = ["sales", "service", "procurement"] as const;

export type PortalModule = (typeof PORTAL_MODULES)[number];

export const PORTAL_MODULE_LABELS: Record<PortalModule, string> = {
  sales: "Sales",
  service: "Service",
  procurement: "Procurement & Inventory",
};

export const PORTAL_MODULE_HOME_PATHS: Record<PortalModule, string> = {
  sales: "/sales",
  service: "/",
  procurement: "/procurement",
};

export function isPortalModule(value: string | null | undefined): value is PortalModule {
  return PORTAL_MODULES.includes(value as PortalModule);
}

export function normalizePortalModule(value: string | null | undefined): PortalModule | null {
  const t = String(value ?? "").trim().toLowerCase();
  return isPortalModule(t) ? t : null;
}

/** First authorised module in Sales → Service → Procurement order. */
export function firstAccessiblePortalModule(
  canAccess: (module: PortalModule) => boolean,
): PortalModule | null {
  for (const m of PORTAL_MODULES) {
    if (canAccess(m)) return m;
  }
  return null;
}

/**
 * Resolve default start module: saved preference if still authorised, else first accessible.
 */
export function resolveDefaultPortalModule(input: {
  saved: string | null | undefined;
  canAccess: (module: PortalModule) => boolean;
}): PortalModule | null {
  const saved = normalizePortalModule(input.saved);
  if (saved && input.canAccess(saved)) return saved;
  return firstAccessiblePortalModule(input.canAccess);
}

export function homePathForPortalModule(module: PortalModule | null | undefined): string {
  if (!module) return "/";
  return PORTAL_MODULE_HOME_PATHS[module] ?? "/";
}

/** True when pathname is the bare portal root (not a deep route). */
export function isBarePortalRootPath(pathname: string): boolean {
  const path = (pathname.split("?")[0] ?? pathname).replace(/\/+$/, "") || "/";
  return path === "/" || path === "";
}

/** Infer module from a deep path — used for switcher active state, not forced redirects. */
export function inferPortalModuleFromPath(pathname: string): PortalModule | null {
  const path = pathname.split("?")[0] ?? pathname;
  if (path === "/procurement" || path.startsWith("/procurement/")) return "procurement";
  if (
    path === "/sales" ||
    path.startsWith("/sales/") ||
    path === "/catalog" ||
    path === "/catalogue" ||
    path.includes("/dtools-json-upload/")
  ) {
    return "sales";
  }
  // Customers and Club bookings are shared; preserve the current module.
  if (path === "/customers" || path.startsWith("/customers/")) return null;
  if (path === "/club-bookings" || path.startsWith("/club-bookings/")) return null;
  return "service";
}
